import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Payment as PaymentIconMui, Settings as SettingsIcon } from '@mui/icons-material';
import PaymentIcon from '../ui/PaymentIcon';
import { usePaymentsAll } from '../../hooks/queries/usePayments';
import PaymentMethodsManagerDialog from '../payments/PaymentMethodsManagerDialog';
import placeServicePaymentsService from '../../services/placeServicePaymentsService';

/**
 * PlaceServicePaymentDialog
 * 
 * Dialog para configurar formas de pagamento aceitas por um serviço específico no local.
 * 
 * Features:
 * - Lista todas as formas de pagamento disponíveis
 * - Permite marcar/desmarcar quais são aceitas
 * - Permite definir valor customizado por forma de pagamento
 * - Valor padrão herdado do PlaceService
 */
export default function PlaceServicePaymentDialog({
  open,
  onClose,
  placeService,
  onUpdate
}) {
  const { data: allPayments = [], isLoading: loading } = usePaymentsAll({
    enabled: open && !!placeService,
  });
  const [selectedPayments, setSelectedPayments] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [managerOpen, setManagerOpen] = useState(false);

  // Carregar formas de pagamento já configuradas para este serviço
  useEffect(() => {
    if (open && placeService && allPayments.length > 0) {
      loadExistingPayments();
    }
  }, [open, placeService, allPayments]);

  const loadExistingPayments = async () => {
    try {
      setError(null);

      // Carregar pagamentos já configurados para este serviço
      const existingPayments = await placeServicePaymentsService.getByPlaceAndService(
        placeService.placeId,
        placeService.serviceId || placeService.id
      );

      // Montar objeto com estado inicial
      const paymentsState = {};
      allPayments.forEach(payment => {
        const existing = existingPayments.find(ep => ep.paymentId === payment.id);
        paymentsState[payment.id] = {
          id: existing?.id || null,
          paymentId: payment.id,
          name: payment.name,
          isSelected: !!existing,
          defaultPrice: existing?.defaultPrice || placeService.price || 0,
          originalPrice: placeService.price || 0
        };
      });

      setSelectedPayments(paymentsState);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError('Erro ao carregar formas de pagamento');
    }
  };

  const handleTogglePayment = (paymentId) => {
    setSelectedPayments(prev => ({
      ...prev,
      [paymentId]: {
        ...prev[paymentId],
        isSelected: !prev[paymentId].isSelected
      }
    }));
  };

  const handlePriceChange = (paymentId, value) => {
    const numericValue = parseFloat(value) || 0;
    setSelectedPayments(prev => ({
      ...prev,
      [paymentId]: {
        ...prev[paymentId],
        defaultPrice: numericValue
      }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Iterar sobre as formas de pagamento
      for (const paymentId in selectedPayments) {
        const payment = selectedPayments[paymentId];

        if (payment.isSelected) {
          // Se está selecionado
          if (payment.id) {
            // Atualizar existente (rota nested)
            // Backend espera: { defaultPrice }
            await placeServicePaymentsService.update(payment.id, {
              placeId: placeService.placeId,
              serviceId: placeService.serviceId || placeService.id,
              defaultPrice: payment.defaultPrice
            });
          } else {
            // Criar novo (rota nested)
            // Backend espera: { paymentId, defaultPrice }
            await placeServicePaymentsService.create({
              placeId: placeService.placeId,
              serviceId: placeService.serviceId || placeService.id,
              paymentId: payment.paymentId,
              defaultPrice: payment.defaultPrice
            });
          }
        } else {
          // Se está desmarcado e tinha ID, deletar (rota nested)
          if (payment.id) {
            await placeServicePaymentsService.delete(
              placeService.placeId,
              placeService.serviceId || placeService.id,
              payment.id
            );
          }
        }
      }

      if (onUpdate) {
        await onUpdate();
      }
      
      onClose();
    } catch (err) {
      console.error('Erro ao salvar configurações:', err);
      setError('Erro ao salvar: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <PaymentIconMui color="primary" />
          <Box flex={1}>
            <Typography variant="h6">
              Formas de Pagamento
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {placeService?.service?.name || 'Serviço'}
            </Typography>
          </Box>
          <Tooltip title="Gerenciar formas de pagamento">
            <IconButton
              size="small"
              onClick={() => setManagerOpen(true)}
              sx={{ color: 'text.secondary' }}
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Valor padrão do serviço: <strong>R$ {placeService?.price?.toFixed(2) || '0.00'}</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Você pode definir valores diferentes para cada forma de pagamento
              </Typography>
            </Alert>

            {allPayments.length === 0 ? (
              <Alert severity="warning">
                Nenhuma forma de pagamento cadastrada.
                Cadastre formas de pagamento primeiro.
              </Alert>
            ) : (
              <Box>
                {allPayments.map((payment) => {
                  const paymentState = selectedPayments[payment.id] || {};
                  const isSelected = paymentState.isSelected || false;

                  return (
                    <Box key={payment.id} sx={{ mb: 2 }}>
                      <Box display="flex" alignItems="flex-start" gap={2}>
                        {/* Ícone do pagamento (sprite para globais, MUI para customizados) */}
                        <Box sx={{ pt: 0.5 }}>
                          {payment.isGlobal ? (
                            <PaymentIcon displayOrder={payment.displayOrder || 1} size={32} />
                          ) : (
                            <Box 
                              sx={{ 
                                width: 32, 
                                height: 21.33, // Proporção 3:2
                                borderRadius: 0.5, 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                bgcolor: 'primary.main',
                                color: 'white',
                                fontSize: '1rem'
                              }}
                            >
                              <PaymentIconMui sx={{ fontSize: '1rem' }} />
                            </Box>
                          )}
                        </Box>
                        
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isSelected}
                              onChange={() => handleTogglePayment(payment.id)}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body1">
                                {payment.name}
                              </Typography>
                              {payment.description && (
                                <Typography variant="caption" color="text.secondary">
                                  {payment.description}
                                </Typography>
                              )}
                            </Box>
                          }
                          sx={{ flex: 1 }}
                        />
                        
                        <TextField
                          size="small"
                          type="number"
                          value={paymentState.defaultPrice || 0}
                          onChange={(e) => handlePriceChange(payment.id, e.target.value)}
                          disabled={!isSelected}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                          }}
                          sx={{ width: 120 }}
                          inputProps={{ 
                            min: 0, 
                            step: 0.01,
                            style: { textAlign: 'right' }
                          }}
                        />
                      </Box>
                      <Divider sx={{ mt: 2 }} />
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || loading}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? 'Salvando...' : 'Salvar'}
        </Button>
      </DialogActions>

      <PaymentMethodsManagerDialog
        open={managerOpen}
        onClose={() => setManagerOpen(false)}
      />
    </Dialog>
  );
}

PlaceServicePaymentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  placeService: PropTypes.object,
  onUpdate: PropTypes.func,
};
